/**
 * Data Transformation Utilities for TPRA Vendors
 *
 * Transforms API responses into UI-compatible formats by:
 * - Adding computed fields for display and searching
 * - Handling optional/nullable fields gracefully
 * - Adding backward-compatible aliases
 */

import type { TPRAVendorDetails, TPRAVendorsResponse } from '../types/vendors';

/**
 * Transforms a single vendor record from API format to UI format
 *
 * Handles real-world data quality issues:
 * - Empty/whitespace vendor_name → empty string
 * - Empty risk_rating → undefined
 * - Null last_assessed_at → empty string
 * - String created_by (user ID) → resolved user object via memberIdNameMapping
 *
 * Adds computed fields:
 * - `vendor_product_name`: Concatenated vendor + product name
 * - `status`: Status field for display (uses assessment_status directly)
 * - `risk`: Normalized risk rating
 * - `last_assessed`: Normalized field name
 * - `created_by_user`: User object resolved from member mapping
 * - `uid`: Alias for `id`
 *
 * @param vendor - Raw vendor data from API
 * @param memberIdNameMapping - Optional mapping of member IDs to names
 * @returns Enhanced vendor data with computed UI fields
 *
 * @example
 * ```typescript
 * const apiVendor = {
 *   id: '123',
 *   vendor_name: '   ', // Whitespace
 *   product_name: 'AI',
 *   risk_rating: '', // Empty
 *   last_assessed_at: null,
 *   created_by: 'user-id-string',
 *   assessment_status: 'intake_form',
 *   // ... other fields
 * };
 *
 * const mapping = { 'user-id-string': 'John Doe' };
 * const uiVendor = transformVendorDetails(apiVendor, mapping);
 * // Result:
 * // {
 * //   ...apiVendor,
 * //   vendor_product_name: 'AI',
 * //   status: 'intake_form',
 * //   risk: undefined,
 * //   last_assessed: '',
 * //   created_by_user: { name: 'John Doe', avatar_url: undefined },
 * //   uid: '123'
 * // }
 * ```
 */
export function transformVendorDetails(
  vendor: TPRAVendorDetails,
  memberIdNameMapping?: Record<string, string>
): TPRAVendorDetails {
  // Handle empty/whitespace vendor names
  const vendorName = vendor.vendor_name?.trim() || '';
  const productName = vendor.product_name?.trim() || '';
  const vendorProductName = safeVendorProductName(vendorName, productName);

  // Handle empty/invalid risk_rating - return undefined instead of 'Unknown'
  const riskRating = vendor.risk_rating?.trim();
  const risk: 'High' | 'Medium' | 'Low' | undefined =
    riskRating && ['High', 'Medium', 'Low'].includes(riskRating)
      ? (riskRating as 'High' | 'Medium' | 'Low')
      : undefined;

  // Handle created_by string (user ID) - resolve from member mapping
  const createdByName = memberIdNameMapping?.[vendor.created_by];
  const createdByUser = createdByName
    ? {
        name: createdByName,
        avatar_url: undefined,
      }
    : undefined;

  return {
    ...vendor,
    // Computed fields
    vendor_product_name: vendorProductName,
    status: vendor.assessment_status, // Use API status directly for display
    risk,
    created_by_user: createdByUser,
    uid: vendor.id, // Alias for backward compatibility
  };
}

/**
 * Transforms TPRA Vendors API response to UI-compatible format
 *
 * Processes entire API response including:
 * - Transforming each vendor record with computed fields
 * - Preserving pagination metadata
 * - Maintaining response structure
 *
 * @param response - Raw API response with vendor list
 * @param memberIdNameMapping - Optional mapping of member IDs to names
 * @returns Transformed response with UI-compatible vendor data
 *
 * @example
 * ```typescript
 * const apiResponse: TPRAVendorsResponse = {
 *   success: true,
 *   total_vendors: 156,
 *   page: 1,
 *   page_size: 50,
 *   total_pages: 4,
 *   vendors: [{ ... }, { ... }]
 * };
 *
 * const mapping = { 'user-id-1': 'John Doe', 'user-id-2': 'Jane Smith' };
 * const transformed = transformVendorsData(apiResponse, mapping);
 * // All vendors now have computed UI fields with resolved user names
 * ```
 */
export function transformVendorsData(
  response: TPRAVendorsResponse,
  memberIdNameMapping?: Record<string, string>
): TPRAVendorsResponse {
  return {
    ...response,
    vendors: response.vendors.map((vendor) =>
      transformVendorDetails(vendor, memberIdNameMapping)
    ),
  };
}

/**
 * Safely extracts vendor product name, handling missing/null values
 *
 * @param vendorName - Vendor company name (may be empty)
 * @param productName - Product/service name (may be empty)
 * @returns Concatenated name, properly trimmed
 *
 * @example
 * ```typescript
 * safeVendorProductName('Asana', 'AI') // => 'Asana AI'
 * safeVendorProductName('Asana', '') // => 'Asana'
 * safeVendorProductName('', 'AI') // => 'AI'
 * safeVendorProductName('', '') // => ''
 * ```
 */
export function safeVendorProductName(
  vendorName: string,
  productName: string
): string {
  const vendor = vendorName?.trim() || '';
  const product = productName?.trim() || '';

  if (vendor && product) {
    return `${vendor} ${product}`;
  }

  return vendor || product || '';
}

/**
 * Checks if vendor has issues (any severity level)
 *
 * @param vendor - Vendor details with issues_by_severity
 * @returns True if vendor has at least one issue
 *
 * @example
 * ```typescript
 * hasIssues(vendor) // => true if total_issues > 0
 * ```
 */
export function hasIssues(vendor: TPRAVendorDetails): boolean {
  return vendor.total_issues > 0;
}

/**
 * Checks if vendor has high-severity issues (high or critical)
 *
 * @param vendor - Vendor details with issues_by_severity
 * @returns True if vendor has high or critical issues
 *
 * @example
 * ```typescript
 * hasHighSeverityIssues(vendor) // => true if critical or high > 0
 * ```
 */
export function hasHighSeverityIssues(vendor: TPRAVendorDetails): boolean {
  const { critical, high } = vendor.issues_by_severity;
  return critical > 0 || high > 0;
}

/**
 * Gets the highest severity level present in vendor's issues
 *
 * @param vendor - Vendor details with issues_by_severity
 * @returns Highest severity level or null if no issues
 *
 * @example
 * ```typescript
 * getHighestSeverityLevel(vendor) // => 'critical' | 'high' | 'medium' | 'low' | null
 * ```
 */
export function getHighestSeverityLevel(
  vendor: TPRAVendorDetails
): 'critical' | 'high' | 'medium' | 'low' | null {
  const { critical, high, medium, low } = vendor.issues_by_severity;

  if (critical > 0) return 'critical';
  if (high > 0) return 'high';
  if (medium > 0) return 'medium';
  if (low > 0) return 'low';

  return null;
}

/**
 * Calculates total number of issues for a vendor
 * (Redundant with total_issues field, but useful for validation)
 *
 * @param vendor - Vendor details with issues_by_severity
 * @returns Sum of all severity levels
 *
 * @example
 * ```typescript
 * calculateTotalIssues(vendor) // => sum of critical + high + medium + low
 * ```
 */
export function calculateTotalIssues(vendor: TPRAVendorDetails): number {
  const { critical, high, medium, low } = vendor.issues_by_severity;
  return critical + high + medium + low;
}

/**
 * Validates if vendor's total_issues matches issues_by_severity sum
 * Useful for API response validation
 *
 * @param vendor - Vendor details
 * @returns True if total_issues equals sum of severity counts
 *
 * @example
 * ```typescript
 * validateIssuesCounts(vendor) // => true if totals match
 * ```
 */
export function validateIssuesCounts(vendor: TPRAVendorDetails): boolean {
  const calculatedTotal = calculateTotalIssues(vendor);
  return vendor.total_issues === calculatedTotal;
}

/**
 * Formats last assessed date relative to now (e.g., "2 days ago")
 * Uses dayjs for consistent date handling
 *
 * @param lastAssessed - ISO 8601 date string from API
 * @returns Human-readable relative time string
 *
 * @example
 * ```typescript
 * formatLastAssessed('2024-12-20T10:00:00Z') // => 'a few days ago'
 * ```
 */
export function formatLastAssessed(lastAssessed: string): string {
  const dayjs = require('dayjs');
  const relativeTime = require('dayjs/plugin/relativeTime');
  const utc = require('dayjs/plugin/utc');
  const timezone = require('dayjs/plugin/timezone');

  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(relativeTime);

  if (!dayjs(lastAssessed).isValid()) {
    return '';
  }

  const parsedDate = dayjs.utc(lastAssessed.replace('T', ' ').split('.')[0]);
  const localDate = parsedDate.local();

  return localDate.fromNow();
}

/**
 * Checks if vendor has a logo (either URL or icon identifier)
 *
 * @param vendor - Vendor details
 * @returns True if vendor has logo_url or icon
 *
 * @example
 * ```typescript
 * hasVendorLogo(vendor) // => true if logo_url or icon present
 * ```
 */
export function hasVendorLogo(vendor: TPRAVendorDetails): boolean {
  return !!(vendor.vendor_logo_url || vendor.vendor_icon);
}

/**
 * Checks if vendor has "Created By" information
 *
 * @param vendor - Vendor details
 * @returns True if created_by_user data is present
 *
 * @example
 * ```typescript
 * hasCreatedBy(vendor) // => true if created_by_user exists with name
 * ```
 */
export function hasCreatedBy(vendor: TPRAVendorDetails): boolean {
  return !!vendor.created_by_user && !!vendor.created_by_user.name;
}

/**
 * Gets initials from a user's name for avatar fallback
 *
 * @param name - Full name (e.g., "John Doe")
 * @returns Initials (e.g., "JD")
 *
 * @example
 * ```typescript
 * getInitials('Grayson Bell') // => 'GB'
 * getInitials('John') // => 'J'
 * getInitials('') // => '?'
 * ```
 */
export function getInitials(name: string): string {
  if (!name || !name.trim()) {
    return '?';
  }

  const parts = name.trim().split(/\s+/);

  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  return (
    parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase()
  );
}
